"use client";

import React, { useState, useEffect, useId } from "react";
import {
  HardDrive,
  Cpu,
  Layers,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Smartphone,
  RefreshCw,
} from "lucide-react";

interface ModelPreset {
  id: string;
  name: string;
  params: string;
  downloadMb: number;
  vramMb: number;
  multilingual: boolean;
  germanQuality: "Ungenügend" | "Kritisch" | "Brauchbar" | "Gut" | "Poor" | "Critical" | "Usable" | "Good";
  descriptionDe: string;
  descriptionEn: string;
}

const MODEL_PRESETS: ModelPreset[] = [
  {
    id: "smollm2-360m",
    name: "SmolLM2-360M (q4f16)",
    params: "360M",
    downloadMb: 204,
    vramMb: 376,
    multilingual: false,
    germanQuality: "Ungenügend",
    descriptionDe: "Extrem kompakt, aber fast ausschließlich auf Englisch trainiert. Erzeugt im Deutschen Kauderwelsch.",
    descriptionEn: "Extremely lightweight, but trained almost exclusively on English. Breaks down on multilingual syntax.",
  },
  {
    id: "qwen-05b",
    name: "Qwen2.5-0.5B (q4f16)",
    params: "490M",
    downloadMb: 483,
    vramMb: 711,
    multilingual: true,
    germanQuality: "Kritisch",
    descriptionDe: "Minimale multilinguale Basis. Versteht Deutsch rudimentär, halluziniert stark bei komplexeren Fragen.",
    descriptionEn: "Minimal multilingual baseline. Understands basic German, but drifts and hallucinates on complex prompts.",
  },
  {
    id: "llama-1b",
    name: "Llama-3.2-1B (q4f16)",
    params: "1.2B",
    downloadMb: 695,
    vramMb: 879,
    multilingual: true,
    germanQuality: "Brauchbar",
    descriptionDe: "Solide Sprachqualität, sprengt jedoch auf Mobilgeräten das vertretbare Cache-Budget für Web-Apps.",
    descriptionEn: "Acceptable generation quality, but exceeds reasonable mobile web cache budgets by a wide margin.",
  },
  {
    id: "phi-4-mini",
    name: "Phi-4-mini (q4f16)",
    params: "3.8B",
    downloadMb: 2260,
    vramMb: 3430,
    multilingual: true,
    germanQuality: "Gut",
    descriptionDe: "Hohe Qualität, erfordert aber Desktop-Hardware (>4 GB VRAM) und über 2 GB Download pro Origin.",
    descriptionEn: "High reasoning capacity, but requires desktop GPU class (>4 GB VRAM) and >2 GB download per origin.",
  },
];

interface DeviceCaps {
  webGpuSupported: boolean;
  webGpuAdapterName: string | null;
  storageQuotaMb: number | null;
  storageUsageMb: number | null;
  languageModelApi: boolean;
  isCrossOriginIsolated: boolean;
  isChecking: boolean;
  error: string | null;
}

interface AuditorProps {
  locale?: "de" | "en";
}

export default function PWAStorageAuditor({ locale = "de" }: AuditorProps) {
  const isEn = locale === "en";
  const [selectedModelId, setSelectedModelId] = useState<string>("qwen-05b");
  const [pwaCount, setPwaCount] = useState<number>(3);
  const pwaCountId = useId();
  const [caps, setCaps] = useState<DeviceCaps>({
    webGpuSupported: false,
    webGpuAdapterName: null,
    storageQuotaMb: null,
    storageUsageMb: null,
    languageModelApi: false,
    isCrossOriginIsolated: false,
    isChecking: true,
    error: null,
  });

  const checkCapabilities = async () => {
    setCaps((prev) => ({ ...prev, isChecking: true, error: null }));
    try {
      let webGpu = false;
      let adapterName: string | null = null;
      if (typeof navigator !== "undefined" && "gpu" in navigator) {
        try {
          const adapter = await (navigator as any).gpu.requestAdapter();
          if (adapter) {
            webGpu = true;
            adapterName = adapter.info?.description || adapter.info?.architecture || (isEn ? "Hardware accelerated" : "Hardware-beschleunigt");
          }
        } catch {
          // WebGPU adapter request blocked or unavailable
        }
      }

      let quotaMb: number | null = null;
      let usageMb: number | null = null;
      if (typeof navigator !== "undefined" && navigator.storage?.estimate) {
        try {
          const est = await navigator.storage.estimate();
          if (est.quota) quotaMb = Math.round(est.quota / (1024 * 1024));
          if (est.usage) usageMb = Math.round(est.usage / (1024 * 1024));
        } catch {
          // Storage estimation not allowed
        }
      }

      const hasLanguageModel =
        typeof window !== "undefined" &&
        ("LanguageModel" in window || ("ai" in window && "languageModel" in (window as any).ai));

      const isolated = typeof window !== "undefined" && Boolean(window.crossOriginIsolated);

      setCaps({
        webGpuSupported: webGpu,
        webGpuAdapterName: adapterName,
        storageQuotaMb: quotaMb,
        storageUsageMb: usageMb,
        languageModelApi: hasLanguageModel,
        isCrossOriginIsolated: isolated,
        isChecking: false,
        error: null,
      });
    } catch (err: any) {
      setCaps((prev) => ({
        ...prev,
        isChecking: false,
        error: err?.message || (isEn ? "Error querying browser capabilities" : "Fehler beim Auslesen der Browser-Fähigkeiten"),
      }));
    }
  };

  useEffect(() => {
    checkCapabilities();
  }, []);

  const selectedModel = MODEL_PRESETS.find((m) => m.id === selectedModelId) || MODEL_PRESETS[1];

  // Calculations
  const pwaCodeSizeMb = 15; // Typical PWA asset bundle (HTML, JS, CSS, WASM runtime)
  const singleOriginTotalMb = pwaCodeSizeMb + selectedModel.downloadMb;
  const multiOriginDuplicateMb = selectedModel.downloadMb * pwaCount;
  const multiOriginTotalMb = singleOriginTotalMb * pwaCount;
  const wastedStoragePercent = Math.round(
    ((multiOriginDuplicateMb - selectedModel.downloadMb) / Math.max(1, multiOriginDuplicateMb)) * 100,
  );

  return (
    <div className="w-full rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-900/90 sm:p-7">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-neutral-100 pb-5 dark:border-neutral-800">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400">
            {isEn ? "Live Auditor & Storage Calculator" : "Live-Auditor & Speicher-Rechner"}
          </span>
          <h3 className="text-lg font-bold text-neutral-900 dark:text-white sm:text-xl">
            {isEn ? "PWA Storage Footprint under Same-Origin Isolation" : "PWA-Speicherbedarf bei Same-Origin-Isolation"}
          </h3>
        </div>
        <button
          type="button"
          onClick={checkCapabilities}
          disabled={caps.isChecking}
          className="inline-flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-3.5 py-2 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-100 active:scale-95 disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
          aria-label={isEn ? "Re-evaluate browser capabilities" : "Browser-Fähigkeiten neu abfragen"}
        >
          <RefreshCw className={`h-4 w-4 ${caps.isChecking ? "animate-spin text-purple-600" : ""}`} />
          <span>{isEn ? "Re-check" : "Erneut prüfen"}</span>
        </button>
      </div>

      {/* Grid: Left Simulator / Right Live Device Audit */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Left Column: Model Selection & PWA Count (7 cols) */}
        <div className="flex flex-col gap-5 lg:col-span-7">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              {isEn ? "1. Select in-browser model" : "1. Modell für In-Browser-Chat wählen"}
            </label>
            <div className="mt-2.5 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {MODEL_PRESETS.map((m) => {
                const isSelected = m.id === selectedModelId;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setSelectedModelId(m.id)}
                    className={`flex min-h-11 flex-col justify-start rounded-xl border p-3 text-left transition ${
                      isSelected
                        ? "border-purple-500 bg-purple-50/60 shadow-sm ring-1 ring-purple-500/30 dark:border-purple-500/60 dark:bg-purple-950/30"
                        : "border-neutral-200 bg-white hover:border-neutral-300 dark:border-neutral-800 dark:bg-neutral-900/50 dark:hover:border-neutral-700"
                    }`}
                  >
                    <div className="flex w-full items-center justify-between text-xs font-bold text-neutral-900 dark:text-neutral-100">
                      <span>{m.name}</span>
                      <span className="text-purple-600 dark:text-purple-400">{m.downloadMb} MB</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-neutral-500 dark:text-neutral-400">
                      <span>VRAM: ~{m.vramMb} MB</span>
                      <span>•</span>
                      <span
                        className={
                          m.germanQuality === "Ungenügend"
                            ? "text-rose-600 dark:text-rose-400"
                            : m.germanQuality === "Kritisch"
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-emerald-600 dark:text-emerald-400"
                        }
                      >
                        {isEn
                          ? m.germanQuality === "Ungenügend"
                            ? "Non-EN: Poor"
                            : m.germanQuality === "Kritisch"
                              ? "Non-EN: Critical"
                              : m.germanQuality === "Brauchbar"
                                ? "Non-EN: Usable"
                                : "Non-EN: Good"
                          : `DE: ${m.germanQuality}`}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
              {isEn ? selectedModel.descriptionEn : selectedModel.descriptionDe}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label
                htmlFor={pwaCountId}
                className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400"
              >
                {isEn ? "2. Installed PWAs with on-device AI" : "2. Installierte PWAs mit lokalem KI-Feature"}
              </label>
              <span className="text-sm font-extrabold text-purple-600 dark:text-purple-400">
                {pwaCount} {pwaCount === 1 ? "PWA" : "PWAs"}
              </span>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <input
                id={pwaCountId}
                type="range"
                min="1"
                max="8"
                step="1"
                value={pwaCount}
                onChange={(e) => setPwaCount(parseInt(e.target.value, 10))}
                className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-neutral-200 accent-purple-600 dark:bg-neutral-700"
              />
            </div>
            <div className="mt-2 flex justify-between text-[11px] text-neutral-400">
              <span>{isEn ? "1 PWA (Single app)" : "1 PWA (Einzel-App)"}</span>
              <span>4 PWAs</span>
              <span>{isEn ? "8 PWAs (Multi-app ecosystem)" : "8 PWAs (Multi-App)"}</span>
            </div>
          </div>

          {/* Duplication Result Box */}
          <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-4.5 dark:border-purple-500/30 dark:bg-purple-950/20">
            <div className="flex items-center gap-2 text-xs font-bold text-purple-900 dark:text-purple-200">
              <Layers className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span>
                {isEn ? "Silo Effect: No Cross-Origin Model Cache" : "Silo-Effekt: Kein Origin-übergreifender Modell-Cache"}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="rounded-lg bg-white/80 p-2.5 dark:bg-neutral-900/60">
                <span className="text-[10px] font-semibold uppercase text-neutral-500">
                  {isEn ? "Download per PWA" : "Download pro PWA"}
                </span>
                <p className="text-base font-black text-neutral-900 dark:text-white">{singleOriginTotalMb} MB</p>
                <span className="text-[10px] text-neutral-400">
                  {isEn
                    ? `15 MB code + ${selectedModel.downloadMb} MB model`
                    : `15 MB Code + ${selectedModel.downloadMb} MB KI`}
                </span>
              </div>
              <div className="rounded-lg bg-white/80 p-2.5 dark:bg-neutral-900/60">
                <span className="text-[10px] font-semibold uppercase text-neutral-500">
                  {isEn ? `Total storage (${pwaCount} PWAs)` : `Gesamtspeicher (${pwaCount} PWAs)`}
                </span>
                <p className="text-base font-black text-purple-600 dark:text-purple-400">{multiOriginTotalMb} MB</p>
                <span className="text-[10px] text-neutral-400">
                  {(multiOriginTotalMb / 1024).toFixed(2)} {isEn ? "GB on disk" : "GB auf Gerät"}
                </span>
              </div>
              <div className="col-span-2 sm:col-span-1 rounded-lg bg-white/80 p-2.5 dark:bg-neutral-900/60">
                <span className="text-[10px] font-semibold uppercase text-neutral-500">
                  {isEn ? "Redundant duplication" : "Reine Duplikation"}
                </span>
                <p className="text-base font-black text-rose-600 dark:text-rose-400">
                  {pwaCount > 1 ? `+${multiOriginDuplicateMb - selectedModel.downloadMb} MB` : "0 MB"}
                </p>
                <span className="text-[10px] text-neutral-400">
                  {pwaCount > 1
                    ? isEn
                      ? `${wastedStoragePercent}% redundant weights`
                      : `${wastedStoragePercent}% redundanter Ballast`
                    : isEn
                      ? "Zero duplication for 1 app"
                      : "Keine Redundanz bei 1 App"}
                </span>
              </div>
            </div>

            {pwaCount > 2 && (
              <div className="mt-3 flex items-start gap-2 text-[11px] text-neutral-600 dark:text-neutral-300">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                <span>
                  {isEn
                    ? "Because the web security architecture (Same-Origin Policy) forbids cross-origin CacheStorage access, every single installed PWA redownloads identical tensor weights."
                    : "Da der Browser-Sandbox-Standard (Same-Origin-Policy) den Zugriff auf CacheStorage und IndexedDB anderer Domains verbietet, lädt jede einzelne PWA die identischen Gewichtsmatrizen herunter."}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Live Browser Capability Audit (5 cols) */}
        <div className="flex flex-col gap-4 rounded-xl border border-neutral-200 bg-neutral-50/50 p-4.5 dark:border-neutral-800 dark:bg-neutral-950/40 lg:col-span-5">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-700 dark:text-neutral-300">
            <Smartphone className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            <span>{isEn ? "Live Browser Environment" : "Live-Status deines Browsers"}</span>
          </div>

          <div className="flex flex-col gap-3">
            {/* WebGPU */}
            <div className="flex items-start justify-between rounded-lg border border-neutral-200/80 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-900/80">
              <div className="flex items-start gap-2.5">
                <Cpu className="mt-0.5 h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                <div>
                  <span className="text-xs font-semibold text-neutral-900 dark:text-white">
                    {isEn ? "WebGPU Acceleration" : "WebGPU Beschleunigung"}
                  </span>
                  <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                    {caps.webGpuAdapterName ||
                      (isEn ? "Mandatory for viable token inference" : "Nötig für akzeptable Inferenzraten")}
                  </p>
                </div>
              </div>
              {caps.webGpuSupported ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
              ) : (
                <XCircle className="h-4 w-4 shrink-0 text-rose-500" />
              )}
            </div>

            {/* Storage Quota */}
            <div className="flex items-start justify-between rounded-lg border border-neutral-200/80 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-900/80">
              <div className="flex items-start gap-2.5">
                <HardDrive className="mt-0.5 h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                <div>
                  <span className="text-xs font-semibold text-neutral-900 dark:text-white">
                    {isEn ? "Available Browser Quota" : "Verfügbares Browser-Quota"}
                  </span>
                  <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                    {caps.storageQuotaMb !== null
                      ? isEn
                        ? `${(caps.storageQuotaMb / 1024).toFixed(1)} GB quota (current usage: ${caps.storageUsageMb || 0} MB)`
                        : `${(caps.storageQuotaMb / 1024).toFixed(1)} GB Quota (aktuell belegt: ${caps.storageUsageMb || 0} MB)`
                      : isEn
                        ? "Restricted by storage API"
                        : "Nicht ermittelbar (Storage API limitiert)"}
                  </p>
                </div>
              </div>
              {caps.storageQuotaMb && caps.storageQuotaMb > singleOriginTotalMb ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
              ) : (
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
              )}
            </div>

            {/* Native Prompt API */}
            <div className="flex items-start justify-between rounded-lg border border-neutral-200/80 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-900/80">
              <div className="flex items-start gap-2.5">
                <Layers className="mt-0.5 h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                <div>
                  <span className="text-xs font-semibold text-neutral-900 dark:text-white">
                    {isEn ? "Prompt API (window.ai)" : "Chrome Prompt API (window.ai)"}
                  </span>
                  <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                    {caps.languageModelApi
                      ? isEn
                        ? "Native Gemini Nano detected in browser"
                        : "Natives Gemini Nano im Browser vorhanden"
                      : isEn
                        ? "Unavailable (Mobile & Safari standard)"
                        : "Fehlt (Standardfall auf Mobilgeräten & Safari)"}
                  </p>
                </div>
              </div>
              {caps.languageModelApi ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
              ) : (
                <XCircle className="h-4 w-4 shrink-0 text-neutral-400" />
              )}
            </div>
          </div>

          <div className="mt-auto rounded-lg border border-neutral-200/60 bg-white/70 p-3 text-[11px] leading-relaxed text-neutral-600 dark:border-neutral-800/60 dark:bg-neutral-900/50 dark:text-neutral-400">
            <span className="font-semibold text-neutral-800 dark:text-neutral-200">
              {isEn ? "Engineering Reality: " : "Praxiserkenntnis: "}
            </span>
            {isEn
              ? "Until the Prompt API is standardized across mobile engines, every web application must ship its own weights. On mobile devices with flash constraints, unpersisted caches are evicted swiftly."
              : "Solange die Prompt API nicht mobil standardisiert ist, muss jedes Web-Projekt seinen eigenen Modell-Download mitbringen. Auf Smartphones führt dies bei Speicherknappheit zur schnellen Löschung des Caches."}
          </div>
        </div>
      </div>
    </div>
  );
}
